/**
 * As peças comuns ao recibo e à ordem de pagamento.
 *
 * Papel: reproduz o que os dois documentos da origem dividem (l. 3865–3891,
 * 5410–5444): a folha em `--font-documento` de 12,5 px que pede a página A4
 * sem margem, o "Imprimir / Salvar PDF" fixo no canto, o título em caixa alta,
 * a linha "Referente a", a tabela de itens com o pontilhado até o valor, e a
 * linha única de espera, de falha ou de não encontrado.
 *
 * Como usar: cada página monta a `FolhaDoDocumento` com o respiro dela por
 * `className` — a margem do papel é esse respiro, porque o diálogo de
 * impressão pode zerar a do `@page` — e chama `useDocumentoPronto` com o título
 * da aba quando o documento chegou.
 */
import { type ReactNode } from 'react';

import { useImpressao } from '@/shared/embutido';
import { cn } from '@/shared/lib/utils';
import { MARCA_DO_DOCUMENTO } from '../documentos';
import { formatarReais } from '../formato';
import { Botao } from './kit/botao';

/**
 * A folha do documento, com o botão de imprimir.
 *
 * @param className - O respiro da folha: `16mm 15mm` no recibo, `18mm 16mm` na
 *   ordem.
 * @param children - O documento.
 * @returns A página inteira, em branco, com o botão escondido no papel.
 */
export function FolhaDoDocumento({
  className,
  children,
}: {
  className: string;
  children: ReactNode;
}) {
  const imprimir = useImpressao(MARCA_DO_DOCUMENTO);
  return (
    <div className="min-h-screen bg-superficie leading-[normal]">
      <button
        type="button"
        onClick={() => void imprimir()}
        className="fixed top-3.5 right-3.5 cursor-pointer [border-radius:revert] [background-color:revert] px-4 py-2 font-[sans-serif] text-[13px] [color:revert] [border:revert] print:hidden"
      >
        Imprimir / Salvar PDF
      </button>
      <div
        className={cn(
          'font-documento text-[12.5px] text-emb-doc-texto [page:folha-a4-retrato]',
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * O título e a linha "Referente a" do alto do documento.
 *
 * @param titulo - "Recibo de Pagamento" ou "Ordem de Pagamento".
 * @param referencia - A linha de `referenteA`.
 * @param className - O espaço abaixo da referência: 20 px no recibo, 26 px na
 *   ordem.
 * @returns O `h1` e a linha.
 */
export function TopoDoDocumento({
  titulo,
  referencia,
  className,
}: {
  titulo: string;
  referencia: string;
  className: string;
}) {
  return (
    <>
      <h1 className="m-0 mb-1 text-center text-[15px] font-bold tracking-[2.5px] uppercase">
        {titulo}
      </h1>
      <div
        className={cn(
          'text-center font-documento-rotulo text-[10px] tracking-[0.08em] text-emb-doc-rotulo uppercase',
          className,
        )}
      >
        {referencia}
      </div>
    </>
  );
}

/**
 * A tabela de itens, com o pontilhado que vai da descrição ao valor.
 *
 * @param itens - A descrição e o valor de cada linha.
 * @param className - A margem de baixo: 26 px no recibo, 20 px na ordem.
 * @returns A tabela.
 */
export function ItensDoDocumento({
  itens,
  className,
}: {
  itens: readonly { descricao: string; valor: number }[];
  className: string;
}) {
  return (
    <table className={cn('w-full border-collapse', className)}>
      <tbody>
        {itens.map((item, indice) => (
          // As linhas de um documento não mudam de lugar depois de desenhadas.
          <tr key={indice}>
            <td className="overflow-hidden py-1 align-bottom text-[13px] whitespace-nowrap after:ml-1.5 after:inline-block after:w-full after:border-b after:border-dotted after:border-emb-doc-pontilhado after:content-['']">
              {item.descricao}
            </td>
            <td className="py-1 text-right align-bottom text-[13px] whitespace-nowrap tabular-nums">
              {formatarReais(item.valor)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * A linha única no lugar do documento.
 *
 * @param texto - "Carregando…", "Não foi possível carregar." ou a frase do
 *   documento que não existe.
 * @param onTentarDeNovo - Mostra "Tentar de novo" ao lado do texto, só na
 *   falha.
 * @returns A linha centrada na página.
 */
export function AvisoDoDocumento({
  texto,
  onTentarDeNovo,
}: {
  texto: string;
  onTentarDeNovo?: () => void;
}) {
  return (
    <div className="min-h-screen bg-superficie p-10 text-center text-[13px] text-texto-fraco">
      {texto}{' '}
      {onTentarDeNovo !== undefined && (
        <Botao variante="fantasma" tamanho="sm" onClick={onTentarDeNovo}>
          Tentar de novo
        </Botao>
      )}
    </div>
  );
}
